import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from 'antd';
import { all_routes } from '../../../router/all_routes';
import ImageWithBasePath from '../../../../core/common/imageWithBasePath';
import StudentModals from '../studentModals';
import Table from "../../../../core/common/dataTable";
import { RootState } from "../../../../core/data/redux/store";
import { deleteStudentApi, fetchStudentsNewApi } from "../../../../core/data/services/studentService";
import { fetchStudentsSuccess } from "../../../../core/data/redux/studentSlice";
import TooltipOption from '../../../../core/common/tooltipOption';
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import { fetchusersSuccess } from "../../../../core/data/redux/userSlice";
import {
  exportToPDF,
  exportToExcel, 
  ExportColumn,
} from "../../../../utils/exportUtils"
import {getUserDetails} from "../../../../lib/getUserDetails";
import CommonSelect from "../../../../core/common/commonSelect";
import { schoolClassId, classesSectionId } from "../../../../core/common/selectoption/selectoption";


const { Option } = Select;

// Define the filter interface
interface StudentFilters {
  name: string;
  gender: string;
  rollNo: string;
  email: string;
  dob: string;
  schoolClass: string;
  classSection: string;
}

// Initial filter state
const initialFilters: StudentFilters = {
  name: '',
  gender: '',
  rollNo: '',
  email: '',
  dob: '',
  schoolClass: '',
  classSection: '',
};

const StudentGrid = () => {
  const dispatch = useDispatch();
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [viewType,] = useState<'grid' | 'list'>('list');
  const [deleteStudentId, setDeleteStudentId] = useState<number | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);

  // Class and Section selection states
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedClassSectionId, setSelectedClassSectionId] = useState<number | null>(null);

  // Single filter state for all parameters
  const [filters, setFilters] = useState<StudentFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<StudentFilters>(initialFilters);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // For error modal (used both for fetch errors or delete errors)
  const [error, setError] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);

  // For delete success modal
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const students = useSelector((state: RootState) => state.students.students);
  const user = useSelector((state: RootState) => state.user.Users);

  // Get user role
  const getUserRole = useCallback(() => {
    try {
      const userData = getUserDetails();
      if (!userData || userData.length === 0) return null;
      return userData[0].role;
    } catch (error) {
      console.error('Error getting user role:', error);
      return null;
    }
  }, []);

  const userRole = getUserRole();
    const getDashboardRoute = (roleRaw: string) => {
      const role = roleRaw?.toLowerCase()?.trim();
  
      const direct: Record<string, keyof typeof all_routes> = {
        teacher: "teacherDashboard",
        headmaster: "HeadmasterDashboard",
  
      };
  
      if (role in direct) return all_routes[direct[role]];
  
      if (role?.includes("teacher")) return all_routes.teacherDashboard;
      if (role?.includes("headmaster") || role?.includes("hm")) return all_routes.HeadmasterDashboard;
      const fallback: keyof typeof all_routes = "teacherDashboard";
      return all_routes[fallback] ?? "/";
    };
  
  
    const dashboardLink = useMemo(() => getDashboardRoute(userRole), [userRole]);
  

  // Load user data from localStorage if not in Redux
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    const userDetail = localStorage.getItem('userDetail');

    if (accessToken && (!user || user.length === 0) && userDetail) {
      try {
        const parsedUserDetail = JSON.parse(userDetail);
        dispatch(fetchusersSuccess([parsedUserDetail]));
      } catch (error) {
        console.error('Error parsing userDetail from localStorage:', error);
      }
    }
  }, [dispatch, user]);

  // Group sections by class using user data (only for Teachers)
  const groupedClassSections = useMemo(() => {
    if (userRole !== 'Teacher') return {};
    
    try {
      const userData = getUserDetails();
      if (!userData || userData.length === 0) return {};

      const userInfo = userData[0];
      const schoolClasses = userInfo.relatedEntity.schoolClasses || [];
      
      const sections = schoolClasses.reduce((acc: any[], schoolClassObj: any) => {
        if (Array.isArray(schoolClassObj.classSection)) {
          const sections = schoolClassObj.classSection.map((section: any) => ({
            className: schoolClassObj?.schoolClass?.name || schoolClassObj?.name,
            sectionName: section?.name,
            classSectionId: section?.id,
          }));
          return [...acc, ...sections];
        }
        return acc;
      }, []);

      return sections.reduce((grouped: any, section: any) => {
        const { className } = section;
        if (!grouped[className]) {
          grouped[className] = [];
        }
        grouped[className].push(section);
        return grouped;
      }, {});
    } catch (error) {
      console.error('Error extracting class sections:', error);
      return {};
    }
  }, [userRole]);

  // Get unique class names for dropdown
  const classNames = useMemo(() => Object.keys(groupedClassSections), [groupedClassSections]);

  // Get sections for selected class
  const sectionsForSelectedClass = useMemo(() => {
    if (!selectedClass || !groupedClassSections[selectedClass]) return [];
    return groupedClassSections[selectedClass];
  }, [selectedClass, groupedClassSections]);

  // Initialize filtered data when students change
  useEffect(() => {
    setFilteredData(students);
  }, [students]);

  // Fetch students for specific section (Teachers)
  const fetchStudentsBySection = useCallback(async (classSectionId: number) => {
    setIsLoadingStudents(true);
    try {
      const data = await fetchStudentsNewApi({ classSectionId });
      
      // Filter students by classSectionId as an extra safety check
      const filteredStudents = data?.filter((student: any) => 
        student.classSectionId === classSectionId
      ) || [];
      
      dispatch(fetchStudentsSuccess(filteredStudents));
    } catch (error: any) {
      // Handle empty results gracefully
      dispatch(fetchStudentsSuccess([]));
      
      // Only show error for actual API failures, not empty results
      if (error.message !== 'Student not found' && !error.message.includes('not found')) {
        const msg = error?.message ?? "Failed to fetch students.";
        setError(msg);
        setShowErrorModal(true);
        console.error("Error fetching students:", msg);
      }
    } finally {
      setIsLoadingStudents(false);
    }
  }, [dispatch]);

  // Get school ID
  const getSchoolId = useCallback(() => {
    try {
      const userData = getUserDetails();
      if (!userData || userData.length === 0) return null;
      return userData[0]?.relatedEntity?.schools?.id;
    } catch (error) {
      console.error('Error getting school ID:', error);
      return null;
    }
  }, []);

  // Fetch all students for school (Headmaster)
  const fetchAllStudentsForSchool = useCallback(async () => {
    setIsLoadingStudents(true);
    try {
      const schoolId = getSchoolId();
      if (!schoolId) {
        throw new Error('School ID not found');
      }
      
      const data = await fetchStudentsNewApi({ schoolId });
      dispatch(fetchStudentsSuccess(data));
    } catch (error: any) {
      dispatch(fetchStudentsSuccess([]));
      const msg = error?.message ?? "Failed to fetch students.";
      setError(msg);
      setShowErrorModal(true);
      console.error("Error fetching students:", msg);
    } finally {
      setIsLoadingStudents(false);
    }
  }, [dispatch, getSchoolId]);

  // Clear data when switching sections
  const clearSectionData = useCallback(() => {
    dispatch(fetchStudentsSuccess([]));
    setFilteredData([]);
  }, [dispatch]);

  // Handle class change
  const handleClassChange = useCallback((className: string) => {
    clearSectionData();
    setSelectedClass(className);
    setSelectedClassSectionId(null);

    if (groupedClassSections[className] && groupedClassSections[className].length > 0) {
      const firstSection = groupedClassSections[className][0];
      setSelectedClassSectionId(firstSection.classSectionId);
      fetchStudentsBySection(firstSection.classSectionId);
    }
  }, [groupedClassSections, fetchStudentsBySection, clearSectionData]);

  // Handle section change
  const handleSectionClick = useCallback((classSectionId: number) => {
    if (classSectionId === selectedClassSectionId) return;
    
    clearSectionData();
    setSelectedClassSectionId(classSectionId);
    fetchStudentsBySection(classSectionId);
  }, [selectedClassSectionId, fetchStudentsBySection, clearSectionData]);

  // Initialize data based on user role
  useEffect(() => {
    if (userRole === 'Headmaster') {
      // For Headmaster, fetch all students from the school
      fetchAllStudentsForSchool();
    } else if (userRole === 'Teacher') {
      // For Teacher, initialize with first class and section
      if (classNames.length > 0 && !selectedClass) {
        const firstClass = classNames[0];
        setSelectedClass(firstClass);

        if (groupedClassSections[firstClass] && groupedClassSections[firstClass].length > 0) {
          const firstSection = groupedClassSections[firstClass][0];
          setSelectedClassSectionId(firstSection.classSectionId);
          fetchStudentsBySection(firstSection.classSectionId);
        }
      }
    }
  }, [userRole, classNames, selectedClass, groupedClassSections, fetchStudentsBySection, fetchAllStudentsForSchool]);

  // Generic filter update function
  const updateFilter = (key: keyof StudentFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Generate dynamic filter options from actual data
  const getDynamicFilterOptions = () => {
    const nameOptions = [
      { value: '', label: 'All Names' },
      ...Array.from(new Set(students.map((student:any) => 
        `${student.firstName} ${student.lastName}`
      ))).map(name => ({ value: name, label: name }))
    ];

    const genderOptions = [
      { value: '', label: 'All Genders' },
      ...Array.from(new Set(students.map((student:any) => 
        student.gender
      ).filter(Boolean))).map((gender:any) => ({ 
        value: gender, 
        label: gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
      }))
    ];

    const rollNoOptions = [
      { value: '', label: 'All Roll Numbers' },
      ...Array.from(new Set(students.map((student:any) => 
        student.rollNo
      ).filter(Boolean))).map(rollNo => ({ 
        value: rollNo, 
        label: rollNo
      }))
    ];

    return { nameOptions, genderOptions, rollNoOptions };
  };

  // Apply filters based on applied filter states
  // Apply filters based on applied filter states
  const applyFilters = useCallback((filtersToApply: StudentFilters = appliedFilters) => {
    let filtered = students;

    // Filter by name (partial match, case insensitive)
    if (filtersToApply.name) {
      filtered = filtered.filter((student:any) => {
        const fullName = `${student.firstName} ${student.lastName}`;
        return fullName.toLowerCase().includes(filtersToApply.name.toLowerCase());
      });
    }

    // Filter by gender
    if (filtersToApply.gender) {
      filtered = filtered.filter((student:any) => 
        student.gender?.toLowerCase() === filtersToApply.gender.toLowerCase()
      );
    }

    // Filter by roll number (partial match)
    if (filtersToApply.rollNo) {
      filtered = filtered.filter((student:any) => 
        student.rollNo?.toLowerCase().includes(filtersToApply.rollNo.toLowerCase())
      );
    }

    // Filter by email (partial match, case insensitive)
    if (filtersToApply.email) {
      filtered = filtered.filter((student:any) => 
        student.email?.toLowerCase().includes(filtersToApply.email.toLowerCase())
      );
    }

    // Filter by DOB
    if (filtersToApply.dob) {
      filtered = filtered.filter((student:any) => 
        student.dob?.includes(filtersToApply.dob)
      );
    }

    // Filter by Class
    if (filtersToApply.schoolClass) {
      filtered = filtered.filter((student:any) => 
        student.schoolClass === filtersToApply?.schoolClass
      );
    }

    // Filter by Class Section
    if (filtersToApply.classSection) {
      filtered = filtered.filter((student:any) => 
        student?.classSection === filtersToApply?.classSection
      );
    }

    setFilteredData(filtered);
  }, [students, appliedFilters]);

  // Reset filters
  const resetFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setFilteredData(students);
  };

  // Check if any filters are applied
  const hasActiveFilters = useCallback(() => {
    return Object.values(appliedFilters).some(value => value !== '');
  }, [appliedFilters]);

  // Get count of active filters
  const getActiveFilterCount = () => {
    return Object.values(appliedFilters).filter(value => value !== '').length;
  };

  // Only apply filters when students data changes (initial load or data updates)
  useEffect(() => {
    if (!hasActiveFilters()) {
      setFilteredData(students);
    } else {
      applyFilters();
    }
  }, [students, applyFilters, hasActiveFilters]);

  const handleApplyClick = () => {
    // Update applied filter states when Apply button is clicked
    setAppliedFilters(filters);
    
    // Apply filters immediately
    applyFilters(filters);
    
    // Close the dropdown
    dropdownMenuRef.current?.classList.remove('show');
  };

  // handle exports
  const exportColumns: ExportColumn[] = [
    { title: "Roll No", dataIndex: "rollNo" },
    {
      title: "Full Name",
      dataIndex: "firstName",
      render: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    { title: "DOB", dataIndex: "dob" },
    {
      title: "Class & Section",
      dataIndex: "schoolClassId",
      render: (_, row) =>
        `${row.schoolClass}-
           ${row.classSection}`,
    },
    { title: "Gender", dataIndex: "gender" },
  ];

  const handleExportPDF = () =>
    exportToPDF(filteredData, exportColumns, "Students.pdf");
  const handleExportExcel = () =>
    exportToExcel(filteredData, exportColumns, "Students.xlsx");

  // Handler to confirm delete
  const handleConfirmDelete = async () => {
    if (deleteStudentId == null) return;
    try {
      await deleteStudentApi(deleteStudentId);
      
      // Refetch students based on user role
      if (userRole === 'Headmaster') {
        await fetchAllStudentsForSchool();
      } else if (userRole === 'Teacher' && selectedClassSectionId) {
        await fetchStudentsBySection(selectedClassSectionId);
      }

      // SHOW SUCCESS modal
      setSuccessMessage("Student deleted successfully.");
      setShowSuccessModal(true);
    } catch (error: any) {
      const msg = error?.message ?? "An unknown error occurred during deletion.";
      console.error("Error deleting student:", msg);
      setError(msg);
      setShowErrorModal(true);
    } finally {
      setDeleteStudentId(null);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setError("");
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };

  // Get dynamic options
  const { genderOptions } = getDynamicFilterOptions();

  const getStudentImage = (student: any) => {
    return student.profileImage ?? "assets/img/teachers/teacher-01.jpg";
  };

  const columns = useMemo(() => [
    {
      title: "Roll No",
      dataIndex: "rollNo",
      sorter: (a: any, b: any) => a.rollNo.localeCompare(b.rollNo),
    },

    {
      title: "Full Name",
      dataIndex: "firstName",
      render: (text: string, record:any) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-md">
            <ImageWithBasePath
             src={getStudentImage(record)}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </div>
          
          <div className='user-detail ms-2'>
            <p className="text-dark mb-0">{text}, {record.lastName}</p>
            <p className="mb-0">{record.email}</p>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => a.firstName.localeCompare(b.firstName),
    },
     {
      title: "DOB",
      dataIndex: "dob",
      sorter: (a: any, b: any) => a.dob.localeCompare(b.dob),
    },
    {
      title: "Class & Section",
      dataIndex: "schoolClassId",
      render: (_: string, record: any) => (
        `${record.schoolClass}-${record.classSection}`
      ),
      sorter: (a: any, b: any) => a.schoolClassId - b.schoolClassId,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      render: (text: string) => text ? text[0].toUpperCase() + text.slice(1).toLowerCase() : "",
      sorter: (a: any, b: any) => a.gender.localeCompare(b.gender),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <div className="dropdown">
          <Link
            to="#"
            className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="ti ti-dots-vertical fs-14" />
          </Link>
          <ul className="dropdown-menu dropdown-menu-right p-3">
            <li>
              <Link
                className="dropdown-item rounded-1"
                to={all_routes.studentDetail.replace(":id", record.id)}
                state={{ student: record }}
              >
                <i className="ti ti-menu me-2" />View Student
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item rounded-1"
                to={all_routes.editStudent}
                state={{ student: record }}
              >
                <i className="ti ti-edit-circle me-2" />Edit
              </Link>
            </li>
            <li>
              <Link
                className="dropdown-item rounded-1"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                onClick={() => setDeleteStudentId(record.id)}
              >
                <i className="ti ti-trash-x me-2" />Delete
              </Link>
            </li>
          </ul>
        </div>
      )
    }
  ], []);

  return (
    <>
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Students</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to={dashboardLink}>Dashboard</Link></li>
                  <li className="breadcrumb-item active">Students</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link to={all_routes.addStudent} className="btn btn-primary d-flex align-items-center">
                  <i className="ti ti-square-rounded-plus me-2" />
                  Add Student
                </Link>
              </div>
            </div>
          </div>

          {/* Class and Section Selection - Only for Teachers */}
          {userRole === 'Teacher' && (
            <div className="row mb-3 align-items-stretch">
              {/* Class Selection */}
              <div className="col-md-2 col-lg-2">
                <div className="form-group h-100 d-flex flex-column">
                  <label htmlFor="classSelect" className="form-label mb-2">Select Class</label>
                  <div className="flex-grow-1 d-flex align-items-start">
                    <Select
                      id="classSelect"
                      placeholder="Select a class"
                      value={selectedClass}
                      onChange={handleClassChange}
                      className="w-100"
                      size="large"
                      style={{ minHeight: '40px' }}
                      loading={isLoadingStudents}
                    >
                      {classNames.map((className: string) => (
                        <Option key={className} value={className}>
                          {className}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              {/* Sections Selection */}
              <div className="col-md-10 col-lg-10">
                {selectedClass && sectionsForSelectedClass.length > 0 ? (
                  <div className="form-group h-100 d-flex flex-column">
                    <label className="form-label mb-2">
                      Sections for {selectedClass}
                    </label>
                    <div className="flex-grow-1">
                      <div
                        className="d-flex gap-2 pb-2 h-100 align-items-start"
                        style={{
                          overflowX: 'auto',
                          whiteSpace: 'nowrap',
                          scrollbarWidth: 'thin',
                          scrollbarColor: '#888 #f1f1f1',
                          minHeight: '60px'
                        }}
                      >
                        {sectionsForSelectedClass.map((section: any, idx: number) => (
                          <div
                            key={idx}
                            className={`card flex-shrink-0 ${selectedClassSectionId === section.classSectionId ? "border-primary bg-primary text-white" : "border-secondary"
                              }`}
                            style={{
                              cursor: "pointer",
                              minWidth: "100px",
                              width: "100px",
                              height: "40px",
                              transition: "all 0.2s ease"
                            }}
                            onClick={() => handleSectionClick(section.classSectionId)}
                          >
                            <div className="card-body d-flex flex-column justify-content-center align-items-center p-2 h-100">
                              <div className="fw-bold text-center" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                                {section.sectionName}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : selectedClass ? (
                  <div className="form-group h-100 d-flex flex-column">
                    <label className="form-label mb-2">Sections</label>
                    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                      <div className="text-muted">No sections available for {selectedClass}</div>
                    </div>
                  </div>
                ) : (
                  <div className="form-group h-100 d-flex flex-column">
                    <label className="form-label mb-2">Sections</label>
                    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                      <div className="text-muted">Please select a class first</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Role indicator for Headmaster */}
          {userRole === 'Headmaster' && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  <strong>Headmaster View:</strong> Showing all students from your school
                </div>
              </div>
            </div>
          )}

          {/* Filter & Sort */}
          <div className='card'>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Students
                {userRole === 'Teacher' && selectedClassSectionId && (
                  <small className="text-muted ms-2">
                    ({filteredData.length} students in selected section)
                  </small>
                )}
                {userRole === 'Headmaster' && (
                  <small className="text-muted ms-2">
                    ({filteredData.length} students in school)
                  </small>
                )}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* Filter Dropdown */}
                <div className="dropdown mb-3 me-2">
                  <Link 
                    to="#" 
                    className="btn btn-outline-light bg-white dropdown-toggle" 
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" /> Filter
                    {hasActiveFilters() && (
                      <span className="badge badge-primary ms-1">
                        {getActiveFilterCount()}
                      </span>
                    )}
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter Students</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Name</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter student name..."
                                value={filters.name}
                                onChange={(e) => updateFilter('name', e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Roll No</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter roll number..."
                                value={filters.rollNo}
                                onChange={(e) => updateFilter('rollNo', e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Gender</label>
                              <select 
                                className="form-select"
                                value={filters.gender}
                                onChange={(e) => updateFilter('gender', e.target.value)}
                              >
                                {genderOptions.map((option:any) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Email</label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Enter email..."
                                value={filters.email}
                                onChange={(e) => updateFilter('email', e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Date of Birth</label>
                              <input
                                type="date"
                                className="form-control"
                                value={filters.dob}
                                onChange={(e) => updateFilter('dob', e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              <CommonSelect
                                className="select"
                                options={schoolClassId}
                                onChange={(e:any) => {
                                  updateFilter('schoolClass', e?.value)
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                          <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={classesSectionId}
                                onChange={(e:any) => {
                                  updateFilter('classSection', e?.value)
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-between">
                        <div className="text-muted small">
                          Showing {filteredData.length} of {students.length} students
                        </div>
                        <div>
                          <button
                            type="button"
                            onClick={resetFilters}
                            className="btn btn-light me-3"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={handleApplyClick}
                            className="btn btn-primary"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid/List View */}
            <div>
              {viewType === 'list' ? (
                <div className="card-body p-0 py-3">
                  {isLoadingStudents ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading students...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading students for selected section...</p>
                    </div>
                  ) : filteredData.length === 0 ? (
                    <div className="text-center py-5">
                      <i className="ti ti-users-off text-muted" style={{ fontSize: '3rem' }}></i>
                      <h5 className="mt-3 text-muted">No Students Found</h5>
                      <p className="text-muted">
                        {userRole === 'Teacher' && selectedClassSectionId 
                          ? `No students found in section ID: ${selectedClassSectionId}. This section may be empty or not have any enrolled students.`
                          : userRole === 'Teacher'
                          ? 'Please select a class and section to view students'
                          : userRole === 'Headmaster'
                          ? 'No students found in your school. Students may not be enrolled yet.'
                          : 'Please select a class and section to view students'
                        }
                      </p>
                      <div className="mt-3">
                        <button 
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            if (userRole === 'Headmaster') {
                              fetchAllStudentsForSchool();
                            } else if (userRole === 'Teacher' && selectedClassSectionId) {
                              fetchStudentsBySection(selectedClassSectionId);
                            }
                          }}
                        >
                          <i className="ti ti-refresh me-2"></i>
                          Refresh Data
                        </button>
                      </div>
                    </div>
                  ) : (
                    <Table
                      dataSource={filteredData}
                      columns={columns}
                      Selection={true}
                      selectedRowKeys={selectedRowKeys}
                      setSelectedRowKeys={setSelectedRowKeys}
                    />
                  )}
                </div>
              ) : (
                <div className="row">
                  {Array.isArray(filteredData) && filteredData.length > 0 ? filteredData.map((student) => (
                    <div className="col-xxl-3 col-xl-4 col-md-6 d-flex" key={student.id}>
                      {/* card structure from original code */}
                    </div>
                  )) : (
                    <div className="col-12">
                      <div className="alert alert-info text-center">No students found.</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <StudentModals onConfirmDelete={handleConfirmDelete} />

      {/* ERROR modal (for fetch or delete errors) */}
      <ErrorModal
        show={showErrorModal}
        message={error}
        onClose={handleCloseErrorModal}
      />

      {/* SUCCESS modal (for delete success) */}
      <SuccessModal
        show={showSuccessModal}
        message={successMessage}
        onClose={handleCloseSuccessModal}
      />
    </>
  );
};

export default StudentGrid;